import {
  faCaretDown,
  faCaretUp,
  faLockKeyhole,
  faLockOpen,
  faTrashAlt,
} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {ActionIcon, Box, Group, Skeleton, Table, Tooltip} from '@mantine/core';
import {FC, useCallback, useEffect, useMemo} from 'react';
import {useFormContext} from 'react-hook-form';
import {PartItemDetailDto} from '../../../api/generated/index.defs';
import {
  ProtectedElement,
  useRequireAuthorization,
} from '../../../auth/require-authorization';
import {useJobStore} from '../../job-store';
import classes from '../job-evaluation.module.css';
import {EditPartItemRoles, PartsListForm} from './parts-list';
import {usePartsListStore} from './parts-list-store';
import {JobPartsListSchema} from './schema';
import {OptionItemDto} from '../../../api/generics.ts';
import {Select} from '../../../components/inputs/combobox/select.tsx';
import {useOptions} from '../../../hooks/use-options.ts';
import {CodesService} from '../../../api/generated/CodesService.ts';
import {CodeClassTypes} from '../../../api/generated/enums.ts';
import {selectNumberConverter} from '../../../forms/value-converters.ts';

type PartItemRowProps = {
  fieldsLength: number;
  update: (index: number, value: PartItemDetailDto) => void;
  remove: (index: number) => void;
  move: (from: number, to: number) => void;
  moveUp: (index: number) => void;
  moveDown: (index: number) => void;
  index: number;
  isReadonly?: boolean;
  partItemField: PartItemDetailDto;
  partKitNumber?: string;
  actionCodeOptions: OptionItemDto<unknown>[];
  dispositionCodeOptions: OptionItemDto<unknown>[];
  actionCodesIdsWithDispositionRequirement: number[];
};

export const MmJobPartItemRow: FC<PartItemRowProps> = ({
  index,
  update,
  remove,
  move,
  moveUp,
  moveDown,
  isReadonly,
  fieldsLength,
  partItemField,
  partKitNumber,
  actionCodeOptions,
  dispositionCodeOptions,
  actionCodesIdsWithDispositionRequirement,
}) => {
  const isAuthorized = useRequireAuthorization({role: EditPartItemRoles});

  const {
    showAdminFeatures,
    partsWithActionQty,
    addPartWithActionQty,
    removePartWithActionQty,
  } = usePartsListStore((state) => ({
    showAdminFeatures: state.showAdminFeatures,
    partsWithActionQty: state.partsWithActionQty,
    addPartWithActionQty: state.addPartWithActionQty,
    removePartWithActionQty: state.removePartWithActionQty,
  }));

  const {isJobReadonly} = useJobStore((state) => ({
    isJobReadonly: state.isJobReadonly,
  }));

  const {watch, getValues, setValue} = useFormContext<JobPartsListSchema>();
  const watchRowPartItemActionCodeId = watch(
    `partItems.${index}.actionCodeId`,
    0,
  );

  const isActionCodeWithDispositionRequirement = useMemo(() => {
    return actionCodesIdsWithDispositionRequirement.includes(
      watchRowPartItemActionCodeId ?? 0,
    );
  }, [watchRowPartItemActionCodeId, actionCodesIdsWithDispositionRequirement]);

  useEffect(() => {
    if (
      partItemField.qty &&
      partItemField.qty > 1 &&
      partItemField.canBeChanged &&
      isActionCodeWithDispositionRequirement
    ) {
      addPartWithActionQty(index);
      const currentValues = getValues().partItems[index] as PartItemDetailDto;
      if (currentValues.actionQty === undefined) {
        setValue(
          `partItems.${index}.actionQty`,
          partItemField.actionQty ?? partItemField.qty,
        );
      }
    } else {
      removePartWithActionQty(index);
      setValue(`partItems.${index}.actionQty`, undefined);
    }
  }, [
    index,
    partItemField.qty,
    addPartWithActionQty,
    partItemField.canBeChanged,
    isActionCodeWithDispositionRequirement,
    removePartWithActionQty,
    setValue,
    getValues,
    partItemField.actionQty,
  ]);

  const rowItemActionCode = useMemo(
    () =>
      actionCodeOptions.find(
        (item) => item.meta.id === watchRowPartItemActionCodeId,
      ),
    [watchRowPartItemActionCodeId, actionCodeOptions],
  );

  const sequenceColor = useMemo(
    () =>
      typeof rowItemActionCode !== 'string' && rowItemActionCode?.meta
        ? rowItemActionCode.meta.color
        : '',
    [rowItemActionCode],
  );

  return (
    <Table.Tr key={partItemField?.id || `partItem-${index}`}>
      <Table.Td miw={200} w="10%" px={6}>
        <Group gap={0}>
          {!!partItemField.partKitNumber &&
          partItemField.partKitNumber === partKitNumber ? (
            <mark
              key={`${index}-highlight`}
              className={classes.partNumberHighlight}
            >
              {isAuthorized && showAdminFeatures ? (
                <PartsListForm.TextInput
                  name={`partItems.${index}.partNumber`}
                  disabled={isReadonly}
                />
              ) : (
                partItemField.partNumber
              )}
            </mark>
          ) : (
            <>
              {isAuthorized && showAdminFeatures ? (
                <PartsListForm.TextInput
                  name={`partItems.${index}.partNumber`}
                  disabled={isReadonly}
                />
              ) : (
                partItemField.partNumber
              )}
            </>
          )}
        </Group>
      </Table.Td>
      <Table.Td>
        <div key={`short-description-${index}`}>{partItemField.shortDescription}</div>
      </Table.Td>

      <Table.Td w="35%">
        {!partItemField.description ? (
          <>
            <Skeleton
              key={`description-skeleton-1-${index}`}
              animate={false}
              height={6}
              mt={6}
              width={450}
              radius="xl"
            />
            <Skeleton
              key={`description-skeleton-2-${index}`}
              mt={6}
              height={6}
              animate={false}
              width={400}
              radius="xl"
            />
          </>
        ) : (
          <div key={`description-${index}`}>{partItemField.description}</div>
        )}
      </Table.Td>
      <Table.Td w="5%" align="center">
        {!partItemField.class ? (
          <Skeleton
            key={`class-skeleton-${index}`}
            animate={false}
            height={6}
            mb={6}
            radius="xl"
          />
        ) : (
          <>{partItemField.class}</>
        )}
      </Table.Td>
      <Table.Td miw="80" maw="80" align="center">
        {isAuthorized && showAdminFeatures ? (
          <PartsListForm.TextInput
            type="number"
            disabled={isReadonly}
            name={`partItems.${index}.materialSequence`}
            css={getSequenceStyles(
              sequenceColor || partItemField.actionCode?.color || "",

            )}
          />
        ) : (
          <mark
            key={`sequence-highlight-${index}`}
            css={getSequenceStyles(
              sequenceColor || partItemField.actionCode?.color || "",

            )}
          >
            {partItemField.materialSequence}
          </mark>
        )}
      </Table.Td>
      <Table.Td miw={75} w={80} align="center">
        {isAuthorized && showAdminFeatures ? (
          <PartsListForm.NumberInput
            min={0}
            disabled={isReadonly}
            name={`partItems.${index}.qty`}
          />
        ) : (
          partItemField.qty
        )}
      </Table.Td>
      <Table.Td>
        {((partItemField.canBeChangedOverride != null
          ? partItemField.canBeChangedOverride
          : partItemField.canBeChanged) &&
          actionCodeOptions) ||
        !isJobReadonly ? (
          <>
            {isAuthorized && showAdminFeatures ? (
              <Box style={partItemStyles.flex}>
                <PartsListForm.Select
                  disabled={isReadonly}
                  style={partItemStyles.nowrap}
                  name={`partItems.${index}.actionCodeId`}
                  data={actionCodeOptions.map((option) => ({
                    ...option,
                    key: `actionCode-${option.value}`,
                  }))}
                  leftSection={
                    <Tooltip
                      openDelay={600}
                      label={
                        partItemField.canBeChanged
                          ? "Can be changed"
                          : "Can't be changed"
                      }
                    >
                      <ActionIcon
                        size={32}
                        variant="subtle"
                        key={`actionCode-${index}`}
                        onClick={() => {
                          const currentValues = getValues().partItems[
                            index
                          ] as PartItemDetailDto;
                          update(index, {
                            ...currentValues,
                            canBeChangedOverride:
                              partItemField.canBeChangedOverride != null
                                ? !partItemField.canBeChangedOverride
                                : !partItemField.canBeChanged,
                          });
                        }}
                      >
                        {(
                          partItemField.canBeChangedOverride != null
                            ? partItemField.canBeChangedOverride
                            : partItemField.canBeChanged
                        ) ? (
                          <FontAwesomeIcon icon={faLockOpen} opacity={0.5} />
                        ) : (
                          <FontAwesomeIcon icon={faLockKeyhole} />
                        )}
                      </ActionIcon>
                    </Tooltip>
                  }
                />
              </Box>
            ) : (
              <Box style={partItemStyles.flex}>
                <PartsListForm.Select
                  miw="150"
                  style={partItemStyles.nowrap}
                  name={`partItems.${index}.actionCodeId`}
                  data={actionCodeOptions.map((option) => ({
                    ...option,
                    key: `actionCode-${option.value}`,
                  }))}
                  disabled={
                    isReadonly ||
                    (partItemField.canBeChangedOverride != null
                      ? !partItemField.canBeChangedOverride
                      : !partItemField.canBeChanged)
                  }
                />
              </Box>
            )}
          </>
        ) : (
          partItemField.actionCode?.description
        )}
      </Table.Td>
      {partsWithActionQty.length > 0 && (
        <Table.Td miw={75} w={100}>
          {partsWithActionQty.includes(index) && (
            <PartsListForm.NumberInput
              min={1}
              max={partItemField.qty}
              disabled={isReadonly}
              name={`partItems.${index}.actionQty`}
            />
          )}
        </Table.Td>
      )}
      <Table.Td>
        {isActionCodeWithDispositionRequirement ? (
          <PartsListForm.Select
            disabled={isReadonly}
            style={partItemStyles.nowrap}
            name={`partItems.${index}.dispositionCodeId`}
            data={dispositionCodeOptions.map((option) => ({
              ...option,
              key: `disposition-${option.value}`,
            }))}
          />
        ) : (
          <></>
        )}
      </Table.Td>
      <ProtectedElement role={EditPartItemRoles}>
        {showAdminFeatures && (
          <Table.Td p={6} w="5%">
            <ActionIcon.Group>
              <ActionIcon
                size="lg"
                color="red"
                variant="subtle"
                onClick={() => remove(index)}
              >
                <FontAwesomeIcon icon={faTrashAlt} />
              </ActionIcon>
              <ActionIcon size="lg" variant="subtle" p={0}>
                <ActionIcon.Group orientation="vertical">
                  <ActionIcon
                    key={`${index}-up}`}
                    w={35}
                    size="xs"
                    onClick={() => {
                      if (index < 1) {
                        move(index, fieldsLength - 1);
                      } else {
                        moveUp(index);
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faCaretUp} />
                  </ActionIcon>
                  <ActionIcon
                    key={`${index}-down}`}
                    w={35}
                    size="xs"
                    onClick={() => {
                      if (index === fieldsLength - 1) {
                        move(index, 0);
                      } else {
                        moveDown(index);
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faCaretDown} />
                  </ActionIcon>
                </ActionIcon.Group>
              </ActionIcon>
            </ActionIcon.Group>
          </Table.Td>
        )}
      </ProtectedElement>
    </Table.Tr>
  );
};

type NonMmJobPartItemRowProps = {
  fieldsLength: number;
  update: (index: number, value: PartItemDetailDto) => void;
  remove: (index: number) => void;
  move: (from: number, to: number) => void;
  moveUp: (index: number) => void;
  moveDown: (index: number) => void;
  index: number;
  isReadonly?: boolean;
  partItemField: PartItemDetailDto;
  partKitNumber?: string;
};
export const NonMmJobPartItemRow: FC<NonMmJobPartItemRowProps> = ({
  index,
  update,
  remove,
  move,
  moveUp,
  moveDown,
  isReadonly,
  fieldsLength,
  partItemField,
}) => {
  const onMoveDown = useCallback(() => {
    if (index === fieldsLength - 1) {
      move(index, 0);
    } else {
      moveDown(index);
    }
  }, [fieldsLength, index, move, moveDown]);

  const onMoveUp = useCallback(() => {
    if (index < 1) {
      move(index, fieldsLength - 1);
    } else {
      moveUp(index);
    }
  }, [fieldsLength, index, move, moveUp]);

  const dispositionCodeOptions =
    useOptions(CodesService.getDispositionCodeOptions).value ?? [];
    
  const partNameCodeOptions =
    useOptions(() => CodesService.getOptionsByClass({
      codeClass: CodeClassTypes['Part Name'],
    })).value ?? [];

  return (
    <Table.Tr key={partItemField?.id || `partItem-${index}`}>
      <Table.Td>
        <PartsListForm.TextInput
          name={`partItems.${index}.partNumber`}
          disabled={isReadonly}
        />
      </Table.Td>
      <Table.Td>
        {isReadonly && partItemField.partNameCode ? (
          <div>{partItemField.partNameCode.description}</div>
        ) : (
          <PartsListForm.Select
            searchable
            creatable
            name={`partItems.${index}.partNameCodeId`}
            data={partNameCodeOptions}
            disabled={isReadonly}
            placeholder="Select or create part name"
            transformer={selectNumberConverter}
            onCreate={async (description) => {
              const response = await CodesService.quickAdd({
                body: {
                  description,
                  codeClass: CodeClassTypes['Part Name'],
                  inactive: false,
                  sortOrder: 0,
                },
              });
              return response;
            }}
          />
        )}
      </Table.Td>
      <Table.Td>
        <PartsListForm.TextInput name={`partItems.${index}.nonMmLotNumber`} />
      </Table.Td>
      <Table.Td miw={75} w={80} align='center'>
        <PartsListForm.NumberInput
          min={0}
          disabled={isReadonly}
          name={`partItems.${index}.qty`}
        />
      </Table.Td>
      <Table.Td w={150}>
        <Select
          style={partItemStyles.nowrap}
          data={nonMmActionCodeOptions}
          readOnly
          value={'0'}
          onChange={() => {}}
        />
      </Table.Td>
      <Table.Td>
        <PartsListForm.Select
          disabled={isReadonly}
          style={partItemStyles.nowrap}
          name={`partItems.${index}.dispositionCodeId`}
          data={dispositionCodeOptions.map((option) => ({
            ...option,
            key: `disposition-${option.value}`,
          }))}
        />
      </Table.Td>
      {!isReadonly && (
        <Table.Td p={6} w='5%'>
          <ActionIcon.Group>
            <ActionIcon
              size='lg'
              color='red'
              variant='subtle'
              onClick={() => remove(index)}
            >
              <FontAwesomeIcon icon={faTrashAlt} />
            </ActionIcon>
            <ActionIcon size='lg' variant='subtle' p={0}>
              <ActionIcon.Group orientation='vertical'>
                <ActionIcon
                  key={`${index}-up}`}
                  w={35}
                  size='xs'
                  onClick={onMoveUp}
                >
                  <FontAwesomeIcon icon={faCaretUp} />
                </ActionIcon>
                <ActionIcon
                  key={`${index}-down}`}
                  w={35}
                  size='xs'
                  onClick={onMoveDown}
                >
                  <FontAwesomeIcon icon={faCaretDown} />
                </ActionIcon>
              </ActionIcon.Group>
            </ActionIcon>
          </ActionIcon.Group>
        </Table.Td>
      )}
    </Table.Tr>
  );
};

const nonMmActionCodeOptions: OptionItemDto[] = [
  {
    label: 'Replace',
    value: 0,
  },
];

const partItemStyles = {
  nowrap: {
    whiteSpace: 'nowrap',
  },
  flex: {
    display: 'flex',
  },
};

const getSequenceStyles = (actionCodeColor: string) => {
  return {
    backgroundColor: `${actionCodeColor}33`,
    borderRadius: '6px',
    padding: '6px',
  };
};